import React, { useState, useCallback } from 'react';
import { ExcelDropdownButton, FileUploadModal } from 'components/common';
import { showAlertNotification } from 'components/common';
import { ExcelOperationsHandlerProps } from 'types';

const ExcelOperationsHandler: React.FC<ExcelOperationsHandlerProps> = ({
  entityType,
  entityName,
  filterParams = {},
  onRefresh,
  menuPosition = 'left',
  disabled = false,
  uploadTitle,
  uploadSubtitle,
  acceptedFileTypes = '.xlsx,.xls,.xlsm,.xlm,.csv',
  fileTypesHelpText = 'Hỗ trợ các tệp tin xls, xlsm, xlm, xlsx, csv....',
  multiple = false,
  maxFileSize = 5 * 1024 * 1024,
  className = '',
  customHandlers,
}) => {
  // State for managing modals
  const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Default titles if not provided
  const defaultUploadTitle = `NHẬP ${entityName.toUpperCase()} TỪ FILE EXCEL`;
  const defaultUploadSubtitle = `Vui lòng tải lên file Excel chứa danh sách ${entityName.toLowerCase()} cần nhập.`;

  // Calculate final titles
  const finalUploadTitle = uploadTitle || defaultUploadTitle;
  const finalUploadSubtitle = uploadSubtitle || defaultUploadSubtitle;

  // Implementation of Excel operations with loading state management
  const handleExportTemplate = useCallback(async () => {
    if (customHandlers?.onExportTemplate) {
      return customHandlers.onExportTemplate();
    }

    setIsLoading(true);
    try {
      // Here you would call your API service based on entityType
      // For example: await excelService.exportTemplate(entityType);
      console.log(`Exporting ${entityType} template...`);

      showAlertNotification({
        type: 'success',
        title: 'Xuất file mẫu',
        message: 'File mẫu đã được tải xuống',
      });
    } catch (error) {
      console.error(`Error exporting ${entityType} template:`, error);
      showAlertNotification({
        type: 'error',
        title: 'Lỗi xuất file mẫu',
        message: 'Đã xảy ra lỗi khi xuất file mẫu. Vui lòng thử lại sau.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [customHandlers, entityType]);

  const handleExportExcel = useCallback(async () => {
    if (customHandlers?.onExport) {
      return customHandlers.onExport();
    }

    setIsLoading(true);
    try {
      console.log(`Exporting all ${entityType} data...`);
      showAlertNotification({
        type: 'success',
        title: 'Xuất dữ liệu',
        message: 'Dữ liệu đã được xuất ra Excel',
      });
    } catch (error) {
      console.error(`Error exporting ${entityType}:`, error);
      showAlertNotification({
        type: 'error',
        title: 'Lỗi',
        message: 'Xuất dữ liệu thất bại, vui lòng thử lại.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [customHandlers, entityType]);

  const handleExportByView = useCallback(async () => {
    if (customHandlers?.onExportByView) {
      return customHandlers.onExportByView();
    }

    setIsLoading(true);
    try {
      showAlertNotification({
        type: 'success',
        title: 'Xuất dữ liệu',
        message: 'Dữ liệu đã được xuất ra Excel',
      });
    } catch (error) {
      console.error(`Error exporting current view of ${entityType}:`, error);
      showAlertNotification({
        type: 'error',
        title: 'Lỗi',
        message: 'Xuất dữ liệu thất bại, vui lòng thử lại.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [customHandlers, entityType]);

  const handleExportByFilter = useCallback(async () => {
    if (customHandlers?.onExportByFilter) {
      return customHandlers.onExportByFilter();
    }

    setIsLoading(true);
    try {
      // Here you would call your API service with filters
      // For example: await excelService.exportFiltered(entityType, filterParams);
      console.log(`Exporting filtered ${entityType} data to Excel:`, filterParams);

      showAlertNotification({
        type: 'success',
        title: 'Xuất dữ liệu theo bộ lọc',
        message: 'Dữ liệu theo bộ lọc đã được xuất ra Excel',
      });
    } catch (error) {
      console.error(`Error exporting current view of ${entityType}:`, error);
      showAlertNotification({
        type: 'error',
        title: 'Lỗi',
        message: 'Xuất dữ liệu thất bại, vui lòng thử lại.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [customHandlers, entityType, filterParams]);

  const handleUploadExcel = useCallback(
    async (files: File[]): Promise<void> => {
      if (customHandlers?.onUploadExcel) {
        return customHandlers.onUploadExcel(files);
      }

      if (!files.length) return Promise.resolve();

      setIsLoading(true);
      try {
        console.log(`Processing ${entityType} upload files:`, files);

        return Promise.resolve();
      } catch (error) {
        console.error(`Error uploading ${entityType} Excel:`, error);
        showAlertNotification({
          type: 'error',
          title: 'Lỗi nhập dữ liệu',
          message: 'Đã xảy ra lỗi khi xử lý file Excel. Vui lòng kiểm tra định dạng file và thử lại.',
        });
        return Promise.reject(error);
      } finally {
        setIsLoading(false);
      }
    },
    [customHandlers, entityType],
  );

  const handleUploadComplete = useCallback(
    async (files: File[], key?: string[]): Promise<void> => {
      if (customHandlers?.onUploadComplete) {
        return customHandlers.onUploadComplete(files, key);
      }
      try {
        showAlertNotification({
          type: 'success',
          title: 'Nhập dữ liệu thành công',
          message: `File Excel đã được tải lên và xử lý thành công`,
        });
      } catch (error) {
        showAlertNotification({
          type: 'error',
          title: 'Lỗi',
          message: `Vui lòng nhập đúng thông tin yêu cầu.`,
        });
        console.error(`Error completing ${entityType} upload:`, error);
      } finally {
        setIsUploadModalOpen(false);
      }
    },
    [customHandlers, entityType, onRefresh],
  );

  // Handle import action from dropdown
  const handleImportExcel = () => {
    setIsUploadModalOpen(true);
  };

  // Handle close upload modal
  const handleCloseUploadModal = () => {
    setIsUploadModalOpen(false);
  };

  return (
    <>
      {/* Excel Dropdown Button */}
      <ExcelDropdownButton
        onExportTemplate={handleExportTemplate}
        onImport={handleImportExcel}
        onExport={handleExportExcel}
        onExportByView={handleExportByView}
        onExportByFilter={handleExportByFilter}
        disabled={disabled || isLoading}
        className={className}
        position={menuPosition}
      />

      {/* File Upload Modal */}
      <FileUploadModal
        isOpen={isUploadModalOpen}
        onClose={handleCloseUploadModal}
        onUpload={handleUploadExcel}
        onComplete={handleUploadComplete}
        title={finalUploadTitle}
        subtitle={finalUploadSubtitle}
        acceptedFileTypes={acceptedFileTypes}
        fileTypesHelpText={fileTypesHelpText}
        multiple={multiple}
        maxFileSize={maxFileSize}
      />
    </>
  );
};

export default ExcelOperationsHandler;
